import { Table, Text } from '@mantine/core';
import React from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { textOrNotRecorded } from '../notRecorded';

export type CashflowColumn<T> = {
  key: keyof T;
  header: string;
  align?: 'left' | 'right';
  renderValue?: (value: T[keyof T], item: T) => React.ReactNode;
};

type CashflowTableProps<T> = {
  items: T[];
  columns: CashflowColumn<T>[];
  onRowClick?: (item: T) => void;
};

const CELL_PADDING = 8;

/** Matches the Financial tab's rows: borderless, tight, with the gap carried by row spacing. */
export default function CashflowTable<T extends { id: string }>({
  items,
  columns,
  onRowClick,
}: CashflowTableProps<T>) {
  return (
    <Table
      withRowBorders={false}
      verticalSpacing={0}
      horizontalSpacing={0}
      style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}
      styles={{ thead: { backgroundColor: 'transparent' } }}
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map(column => (
            <Table.Th
              key={String(column.key)}
              style={{ padding: CELL_PADDING, textAlign: column.align ?? 'left' }}
            >
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.5">
                {column.header}
              </Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.map(item => (
          <Table.Tr
            key={item.id}
            style={{ cursor: onRowClick ? 'pointer' : undefined }}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
          >
            {columns.map(column => (
              <RowCell key={String(column.key)} align={column.align}>
                {renderCell(column, item)}
              </RowCell>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function RowCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <Table.Td style={{ padding: CELL_PADDING, textAlign: align }}>
      {/* A div, not the default <p>: cells can hold a tooltip that renders its own
          paragraph, and a paragraph cannot nest inside another. */}
      <Text component="div" size="xs" fw={FONT_WEIGHTS.medium} c="black">
        {children || '—'}
      </Text>
    </Table.Td>
  );
}

function renderCell<T>(column: CashflowColumn<T>, item: T) {
  if (column.renderValue) return column.renderValue(item[column.key], item);
  return textOrNotRecorded(item[column.key]);
}
